import type { Variants } from 'motion/react'
import { PropsWithChildren, useEffect } from 'react'
import { useListEvents } from '@cocoonspace/sdk-js/domains/events/events'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Empty,
	EmptyContent,
	EmptyHeader,
	EmptyMedia,
} from '@cocoonspace/ui/components/empty'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@cocoonspace/ui/components/sheet'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Plural, useLingui } from '@lingui/react/macro'
import { ArrowRightIcon, CalendarOffIcon, ShoppingCartIcon } from 'lucide-react'
import { motion } from 'motion/react'
import Link from 'next/link'

import { Price } from '~/components/price'
import { ViewGroupBy } from '~/domains/booking/components/booking-event-list/view-group-by'
import { CartEventItem } from './cart-event-item'
import { useCart } from './cart-provider'

const cartRowMotionDelay = 0.4
// const cartRowMotionDelay = 0.12

const cartRowVariants = {
	initial: { opacity: 0, scale: 0.88 },
	animate: {
		opacity: 1,
		scale: 1,
		transition: {
			opacity: {
				duration: 0.2,
				ease: 'easeOut',
				delay: cartRowMotionDelay,
			},
			scale: {
				type: 'spring',
				stiffness: 280,
				damping: 11,
				mass: 0.55,
				delay: cartRowMotionDelay,
			},
		},
	},
	exit: {
		opacity: 0,
		scale: 0.88,
		transition: {
			opacity: { duration: 0.15, ease: 'easeIn' },
			scale: {
				type: 'spring',
				stiffness: 400,
				damping: 22,
				mass: 0.5,
			},
		},
	},
} satisfies Variants

const cartRowBgVariants = {
	initial: { opacity: 0 },
	animate: {
		opacity: [0, 1, 0],
		transition: {
			duration: 0.5,
			ease: 'easeOut',
			times: [0, 0.35, 1],
			delay: cartRowMotionDelay,
		},
	},
	exit: { opacity: 0, transition: { duration: 0.1 } },
} satisfies Variants

const EmptyCart = () => {
	const { t } = useLingui()

	return (
		<Empty className='flex flex-1 flex-col items-center justify-center p-4'>
			<EmptyHeader>
				<EmptyMedia>
					<CalendarOffIcon className='!size-8' />
				</EmptyMedia>
			</EmptyHeader>

			<EmptyContent>
				<p>{t`Votre panier est vide`}</p>
			</EmptyContent>
		</Empty>
	)
}

export const CartButtonContent = () => {
	const { t } = useLingui()
	const { booking, eventsCount, setIsSheetOpen } = useCart()

	const balance = booking?.balance ?? 0
	const vat = booking?.vat ?? 0

	const eventsQuery = useListEvents(
		{ booking: booking?.id, status: ['option'] },
		{ query: { enabled: !!booking?.id } },
	)

	if (!eventsCount) return <EmptyCart />

	return (
		<>
			<div className='flex-1 overflow-y-auto p-4'>
				<ViewGroupBy
					withAnimatePresenceEvents
					events={eventsQuery.data?.data ?? []}
					render={(event) => (
						<motion.div
							key={event.id}
							layout
							className='relative origin-center'
							variants={cartRowVariants}
							initial='initial'
							animate='animate'
							exit='exit'
						>
							<motion.div
								aria-hidden
								className='pointer-events-none absolute -inset-2 rounded-md bg-green-300/30'
								variants={cartRowBgVariants}
								initial='initial'
								animate='animate'
								exit='exit'
							/>
							<CartEventItem event={event} />
						</motion.div>
					)}
				/>
			</div>

			<div className='flex flex-row gap-4 divide-x'>
				<div className='flex flex-1 items-center justify-between gap-x-1 p-4'>
					<span className='text-sm text-gray-500'>{t`Total HT`}</span>
					<Price price={Math.abs(balance) - vat} />
				</div>

				<div className='flex flex-1 items-center justify-between gap-x-1 p-4'>
					<span className='text-sm text-gray-500'>{t`Total TTC`}</span>
					<Price price={Math.abs(balance)} />
				</div>
			</div>

			<SheetFooter className='flex !flex-col items-center justify-between gap-4 p-4'>
				<div className='flex w-full flex-row items-center justify-between gap-x-2'>
					<SheetClose asChild>
						<Button
							variant='ghost'
							size='sm'
							className='flex-1'
						>
							{t`Continuer ma recherche`}
						</Button>
					</SheetClose>

					<Button
						variant='gradient'
						size='sm'
						className='flex-1'
						asChild
						onClick={() => setIsSheetOpen(false)}
					>
						<Link href='/cart'>
							{t`Valider`}
							<ArrowRightIcon />
						</Link>
					</Button>
				</div>
			</SheetFooter>
		</>
	)
}

const Counter = ({ count }: { count: number }) => (
	<span className='text-xxs bg-primary text-primary-foreground absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full'>
		{count}
	</span>
)

const usePutFrontChatBehindSheet = (isOpen: boolean) => {
	useEffect(() => {
		const iframe = document.querySelector<HTMLElement>('#front-chat-iframe')

		if (!iframe) return

		if (isOpen) {
			iframe.style.zIndex = '0'
		} else {
			iframe.style.zIndex = '99999999'
		}
	}, [isOpen])
}

export const CartSheetRoot = ({ children }: PropsWithChildren) => {
	const { isSheetOpen, setIsSheetOpen } = useCart()

	usePutFrontChatBehindSheet(isSheetOpen)

	return (
		<Sheet
			open={isSheetOpen}
			onOpenChange={setIsSheetOpen}
		>
			{children}
			<CartSheetContent />
		</Sheet>
	)
}

export const CartSheetTrigger = ({ className }: { className?: string }) => {
	const { t } = useLingui()
	const isMobile = useIsMobile()
	const { eventsCount } = useCart()

	if (!eventsCount) return null

	return (
		<SheetTrigger asChild>
			<Button
				icon
				size={isMobile ? 'sm' : 'default'}
				variant='ghost'
				className={cn('relative', className)}
				aria-label={t`Ouvrir le panier (${eventsCount} événements)`}
			>
				<Counter count={eventsCount} />
				<ShoppingCartIcon />
			</Button>
		</SheetTrigger>
	)
}

export const CartSheetContent = () => {
	const { t } = useLingui()
	const { eventsCount } = useCart()

	if (!eventsCount) return null

	return (
		<SheetContent
			side='right'
			className='flex flex-col gap-0 divide-y p-0 max-md:w-full'
		>
			<SheetHeader className='p-4 leading-none'>
				<SheetTitle>{t`Mon panier`}</SheetTitle>

				<SheetDescription>
					<Plural
						value={eventsCount}
						one={t`1 événement dans votre panier`}
						other={t`${eventsCount} événements dans votre panier`}
					/>
				</SheetDescription>
			</SheetHeader>

			<CartButtonContent />
		</SheetContent>
	)
}
